import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import Navbar from '../components/Header/Navbar';
import Footer from '../components/Footer';
import Button from '../components/Button';
import Modal from '../components/Modal/Modal';
import MyReelCard from '../components/reels/MyReelCard';
import ReelDeleteConfirmModal from '../components/reels/ReelDeleteConfirmModal';
import { useAuth } from '../contexts/AuthContext';
import { useMyReels } from '../hooks/useMyReels';
import { useDeleteReel } from '../hooks/useDeleteReel';
import type { ReelMineViewModel } from '../api/types/reel';

const Gonderilerim: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { reels, loading, error, refetch, removeReel } = useMyReels(isAuthenticated && Boolean(user?.id));
  const { deleteReel, loading: deleteLoading, error: deleteError, resetError: resetDeleteError } = useDeleteReel();

  const [reelPendingDelete, setReelPendingDelete] = useState<ReelMineViewModel | null>(null);
  const [infoModal, setInfoModal] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/giris', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const requestDelete = useCallback((reel: ReelMineViewModel) => {
    resetDeleteError();
    setReelPendingDelete(reel);
  }, [resetDeleteError]);

  const cancelDelete = useCallback(() => {
    if (deleteLoading) return;
    setReelPendingDelete(null);
    resetDeleteError();
  }, [deleteLoading, resetDeleteError]);

  const confirmDelete = useCallback(async () => {
    if (!reelPendingDelete || deleteLoading) return;

    const reelId = reelPendingDelete.id;
    const result = await deleteReel(reelId);
    if (result.ok) {
      removeReel(reelId);
      setReelPendingDelete(null);
      setInfoModal({
        title: 'Gönderi silindi',
        message: result.message,
      });
      return;
    }

    setInfoModal({
      title: 'Gönderi silinemedi',
      message: result.message,
    });
  }, [reelPendingDelete, deleteLoading, deleteReel, removeReel]);

  if (!isAuthenticated) return null;

  if (!user) {
    return (
      <div className="min-vh-100" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
        <Navbar />
        <main className="container py-4">
          <div className="text-center py-5 text-muted">Yükleniyor...</div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
      <Navbar />

      <main className="container py-4">
        <h1 className="mb-4">Gönderilerim</h1>

        {error && (
          <div className="alert alert-warning mb-3 d-flex flex-wrap align-items-center gap-2" role="alert">
            <span>{error}</span>
            <Button className="btn btn-sm btn-outline-primary" onClick={() => refetch()}>
              Tekrar Dene
            </Button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5 text-muted">Yükleniyor...</div>
        ) : !error && reels.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-video fa-3x text-muted mb-3" />
            <h3>Henüz gönderiniz yok</h3>
            <p className="text-muted">İlk gönderinizi yükleyerek başlayabilirsiniz.</p>
            <Link className="btn btn-primary" to="/gonderiler">
              Gönderi Yükle
            </Link>
          </div>
        ) : (
          <div className="row g-4">
            {reels.map((reel) => (
              <div key={reel.id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                <MyReelCard
                  reel={reel}
                  onDelete={requestDelete}
                  deleteDisabled={deleteLoading}
                />
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />

      {infoModal && (
        <Modal
          title={infoModal.title}
          message={infoModal.message}
          onClose={() => setInfoModal(null)}
        />
      )}

      <ReelDeleteConfirmModal
        show={Boolean(reelPendingDelete)}
        loading={deleteLoading}
        error={deleteError}
        onConfirm={() => void confirmDelete()}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default Gonderilerim;
